// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { FormNumberTextField } from '@/components/form-number-text-field';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  LibraryAddOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  FULFILLMENT_ORDER_MAX_ITEMS,
  FulfillmentOrderFormData,
} from '@/fulfillment/schemas/fulfillment-order-form-schema';
import { ItemSearch } from '@/items/components/item-search';
import { ItemFormEntry } from '@/orders/schemas/item';
import { ProjectFulfillmentBySkuRow } from '@/project/api/types';
import { computeFulfillmentNeed } from '@/project/utilities/compute-fulfillment-need';
import { SparseItem, SparseLocation } from '@/sparse/api/types';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Control, Controller, FieldArrayWithId } from 'react-hook-form';

interface Props {
  control: Control<FulfillmentOrderFormData>;
  fields: FieldArrayWithId<FulfillmentOrderFormData, 'items', 'id'>[];
  currentItems: ItemFormEntry[];
  selectedItems: SparseItem[];
  isProcessed: boolean;
  fromLocation: SparseLocation | null;
  inventoryMap: Map<string, Map<string, number>>;
  isLoadingInventory: boolean;
  isFetchingInventory: boolean;
  /** Per-item-id summary rows from the project fulfillment endpoint. */
  summaryByItemId: Map<string, ProjectFulfillmentBySkuRow>;
  /**
   * Edit-mode contribution from the FO being edited (item ID -> saved
   * requestedQuantity). Subtracted from foRequested so "Fulfilled" reflects
   * coverage from other FOs only.
   */
  ownContributionByItemId: Map<string, number>;
  /** True while the summary fetch is in flight. */
  isSummaryLoading: boolean;
  onAddItem: () => void;
  onAddBundle: () => void;
  onRemoveItem: (index: number) => void;
  /**
   * Called when the item search picker changes. The form owns the quantity
   * default-to-need policy; this component just notifies of the new selection.
   */
  onItemChanged: (index: number, value: SparseItem | null) => void;
  /**
   * Called when the user edits a quantity, so the form marks the manifest
   * user-selected and stops suggestions from overwriting it.
   */
  onQuantityUserSelected: () => void;
}

/**
 * Item picker laid out to mirror the shipment items table when an FO is
 * provided — Item / Requested / Fulfilled / Need / Quantity / actions.
 *
 * The Requested / Fulfilled / Need math lives in `computeFulfillmentNeed` so
 * the values displayed in this table and the auto-defaulted Quantity in the
 * parent form cannot diverge. "Fulfilled" intentionally counts every
 * non-cancelled FO on the project (any phase, including pending) because
 * anything on an FO is coverage that already exists; subtracting the FO
 * under edit avoids double-counting its own row.
 */
export function FulfillmentOrderItemsTable(props: Props) {
  const {
    control,
    fields,
    currentItems,
    selectedItems,
    isProcessed,
    fromLocation,
    inventoryMap,
    isLoadingInventory,
    isFetchingInventory,
    summaryByItemId,
    ownContributionByItemId,
    isSummaryLoading,
    onAddItem,
    onAddBundle,
    onRemoveItem,
    onItemChanged,
    onQuantityUserSelected,
  } = props;

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="subtitle1">Fulfillment order items</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAddItem}
            disabled={
              fields.length >= FULFILLMENT_ORDER_MAX_ITEMS || isProcessed
            }
          >
            Add item
          </Button>
          {/* Stays enabled at the row cap: applying a bundle can still
              auto-sum quantities into existing rows without adding rows */}
          <Button
            variant="outlined"
            startIcon={<LibraryAddOutlined />}
            onClick={onAddBundle}
            disabled={isProcessed}
          >
            Add bundle
          </Button>
        </Stack>
      </Stack>

      <Table
        size="small"
        sx={{
          '& .MuiTableCell-root': { pt: 2.5, pb: 1.5 },
          '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root':
            {
              borderBottom: 'none',
            },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell align="right" sx={{ width: 100 }}>
              Requested
            </TableCell>
            <TableCell align="right" sx={{ width: 100 }}>
              Fulfilled
            </TableCell>
            <TableCell align="right" sx={{ width: 100 }}>
              Need
            </TableCell>
            <TableCell align="right" sx={{ width: 200 }}>
              <Box sx={{ mr: 1.5 }}>Quantity</Box>
            </TableCell>
            <TableCell align="center" sx={{ width: 60 }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((field, index) => {
            const currentValue = currentItems[index];
            const itemId = currentValue?.item?.id;
            const summaryRow = itemId ? summaryByItemId.get(itemId) : undefined;

            const own = itemId ? (ownContributionByItemId.get(itemId) ?? 0) : 0;
            const computed = summaryRow
              ? computeFulfillmentNeed(summaryRow, own)
              : null;
            const requested = summaryRow?.projectRequested ?? null;
            const fulfilled = computed?.fulfilled ?? null;
            const need = computed?.need ?? null;

            // Available inventory caption (same logic as shipments).
            let availableText = '';
            if (currentValue?.item) {
              if (!fromLocation) {
                availableText =
                  "Select 'from' location to show available quantity";
              } else if (isLoadingInventory || isFetchingInventory) {
                availableText = 'Loading...';
              } else {
                const availableQty =
                  inventoryMap
                    .get(fromLocation.id)
                    ?.get(currentValue.item.id) ?? 0;
                availableText = `${formatNumber(availableQty)} available`;
              }
            }

            return (
              <TableRow key={field.id}>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <ConditionalTooltip
                    arrow
                    tooltipText={
                      isProcessed
                        ? 'Items cannot be changed once fulfillment is processed'
                        : undefined
                    }
                    sx={{ width: '100%' }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Controller
                        control={control}
                        name={`items.${index}.item`}
                        render={({ fieldState }) => (
                          <ItemSearch
                            label="Item"
                            value={currentValue?.item ?? null}
                            physicalOnly
                            disabled={isProcessed}
                            onChange={(value) => onItemChanged(index, value)}
                            hiddenItems={selectedItems.filter(
                              (item) => item.id !== currentValue?.item?.id
                            )}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </Box>
                  </ConditionalTooltip>
                </TableCell>

                <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                  <Box sx={{ pt: 1 }}>
                    {renderSummaryNumber(requested, isSummaryLoading)}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                  <Box sx={{ pt: 1 }}>
                    {renderSummaryNumber(fulfilled, isSummaryLoading)}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                  <Box sx={{ pt: 1 }}>
                    {renderSummaryNumber(need, isSummaryLoading)}
                  </Box>
                </TableCell>

                <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                  <FormNumberTextField
                    fullWidth
                    nullable
                    numeric
                    label="Quantity"
                    control={control}
                    name={`items.${index}.quantity`}
                    disabled={isProcessed}
                    onChange={onQuantityUserSelected}
                  />
                  {availableText && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
                    >
                      {availableText}
                    </Typography>
                  )}
                </TableCell>

                <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                  <Box sx={{ pt: 0.5 }}>
                    <ConditionalTooltip
                      arrow
                      tooltipText={
                        isProcessed
                          ? 'Items cannot be removed once fulfillment is processed'
                          : fields.length <= 1
                            ? 'At least one item is required'
                            : undefined
                      }
                    >
                      <Box>
                        <IconButton
                          onClick={() =>
                            fields.length > 1 && onRemoveItem(index)
                          }
                          size="small"
                          disabled={fields.length <= 1 || isProcessed}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ConditionalTooltip>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mr: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Total quantity:{' '}
          <Typography component="span" variant="body2" fontWeight="medium">
            {formatNumber(
              currentItems.reduce((sum, item) => sum + (item.quantity ?? 0), 0)
            )}
          </Typography>
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Renders a summary cell value: skeleton while loading, em-dash when the item
 * isn't on the project's summary, or the formatted number.
 */
function renderSummaryNumber(value: number | null, isLoading: boolean) {
  if (isLoading) {
    return <Skeleton variant="text" width={32} sx={{ ml: 'auto' }} />;
  }
  return (
    <Typography variant="body2">
      {value !== null ? formatNumber(value) : '—'}
    </Typography>
  );
}
